import { isWithinTradingSession } from "./tradingSession";
const pad = (value) => String(value).padStart(2, "0");
const formatDate = (date) => {
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    return `${year}-${month}-${day}`;
};
const mskParts = (date) => {
    const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/Moscow",
        hour: "2-digit",
        minute: "2-digit",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour12: false,
    }).formatToParts(date);
    const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
    const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
    const year = parts.find((p) => p.type === "year")?.value ?? "1970";
    const month = parts.find((p) => p.type === "month")?.value ?? "01";
    const day = parts.find((p) => p.type === "day")?.value ?? "01";
    return { hhmm: `${hour}:${minute}`, ymd: `${year}-${month}-${day}` };
};
const inWindow = (current, start, end) => {
    if (!start || !end)
        return false;
    if (start <= end)
        return current >= start && current <= end;
    return current >= start || current <= end;
};
export const buildFallbackExchangeSchedule = (mode, daysCount = 7) => {
    const days = [];
    const now = new Date();
    for (let offset = 0; offset < daysCount; offset += 1) {
        const date = new Date(now);
        date.setDate(now.getDate() + offset);
        const weekday = date.getDay();
        const isTrading = weekday >= 1 && weekday <= 5;
        days.push({
            date: formatDate(date),
            is_trading_day: isTrading,
            start_time: isTrading ? "10:00" : null,
            end_time: isTrading ? "18:40" : null,
            evening_start_time: isTrading ? "19:00" : null,
            evening_end_time: isTrading ? "23:50" : null,
        });
    }
    return {
        mode,
        exchange: "MOEX",
        timezone: "Europe/Moscow",
        source: "fallback",
        days,
        fetched_at: now.toISOString(),
    };
};
export const isWithinExchangeSchedule = (schedule, now = new Date()) => {
    const local = mskParts(now);
    const day = schedule.days.find((item) => item.date === local.ymd);
    if (!day)
        return { ok: true };
    if (!day.is_trading_day) {
        return { ok: false, message: "Сегодня неторговый день на бирже." };
    }
    if (inWindow(local.hhmm, day.start_time, day.end_time) || inWindow(local.hhmm, day.evening_start_time, day.evening_end_time)) {
        return { ok: true };
    }
    const windows = [];
    if (day.start_time && day.end_time)
        windows.push(`${day.start_time}–${day.end_time}`);
    if (day.evening_start_time && day.evening_end_time) {
        windows.push(`${day.evening_start_time}–${day.evening_end_time}`);
    }
    return {
        ok: false,
        message: `Вне расписания биржи: сейчас ${local.hhmm}, окна ${windows.join(", ")} (${schedule.timezone || "Europe/Moscow"}).`,
    };
};
export const findNextTradingWindow = (schedule, now = new Date()) => {
    const local = mskParts(now);
    const candidates = [];
    for (const day of schedule.days) {
        if (!day.is_trading_day)
            continue;
        for (const [kind, start] of [
            ["main", day.start_time],
            ["evening", day.evening_start_time],
        ]) {
            if (!start)
                continue;
            const at = new Date(`${day.date}T${start}:00+03:00`);
            if (at <= now)
                continue;
            const kindLabel = kind === "main" ? "основная сессия" : "вечерняя сессия";
            let dateLabel = day.date === local.ymd ? "сегодня" : start;
            if (day.date !== local.ymd) {
                const tomorrow = new Date(`${local.ymd}T12:00:00+03:00`);
                tomorrow.setDate(tomorrow.getDate() + 1);
                const tomorrowYmd = formatDate(tomorrow);
                if (day.date === tomorrowYmd) {
                    dateLabel = "завтра";
                }
                else {
                    dateLabel = new Intl.DateTimeFormat("ru-RU", {
                        timeZone: "Europe/Moscow",
                        day: "2-digit",
                        month: "2-digit",
                    }).format(at);
                }
            }
            candidates.push({
                at,
                label: `${dateLabel} в ${start} MSK (${kindLabel})`,
            });
        }
    }
    if (candidates.length === 0)
        return null;
    const next = candidates.sort((a, b) => a.at.getTime() - b.at.getTime())[0];
    return { at: next.at.toISOString(), label: next.label };
};
export const tradingStatusLabel = (status) => {
    const labels = {
        SECURITY_TRADING_STATUS_NORMAL_TRADING: "Нормальная торговля",
        SECURITY_TRADING_STATUS_BREAK_IN_TRADING: "Перерыв в торговле",
        SECURITY_TRADING_STATUS_NOT_AVAILABLE_FOR_TRADING: "Недоступен для торгов",
        SECURITY_TRADING_STATUS_SESSION_CLOSE: "Сессия закрыта",
        SECURITY_TRADING_STATUS_DEALER_NORMAL_TRADING: "Торговля (внутренняя ликвидность)",
    };
    if (!status)
        return "Неизвестно";
    return labels[status] ?? status;
};
const formatBlockReasons = (reasons, nextLabel) => {
    const parts = [];
    if (reasons.length > 0) {
        parts.push(`По тикеру нельзя, потому что:\n${reasons.map((item) => `• ${item}`).join("\n")}`);
    }
    if (nextLabel) {
        parts.push(`Следующее окно биржи: ${nextLabel}.`);
    }
    return parts.join("\n\n");
};
export const mockInstrumentTradability = (ticker, orderType, schedule, agentSchedule) => {
    void ticker;
    const scheduleCheck = isWithinExchangeSchedule(schedule);
    const agentCheck = agentSchedule ? isWithinTradingSession(agentSchedule, new Date(), "окна сессии агента") : { ok: true };
    const nextWindow = findNextTradingWindow(schedule);
    const blockedReasons = [];
    if (!scheduleCheck.ok) {
        blockedReasons.push(scheduleCheck.message);
    }
    if (!agentCheck.ok) {
        blockedReasons.push(agentCheck.message);
    }
    if (blockedReasons.length > 0) {
        return {
            tradable_now: false,
            trading_status: "SECURITY_TRADING_STATUS_NOT_AVAILABLE_FOR_TRADING",
            api_trade_available: false,
            market_order_available: false,
            limit_order_available: false,
            blocked_reasons: blockedReasons,
            next_tradable_at: nextWindow?.at ?? null,
            next_tradable_label: nextWindow?.label ?? null,
            reason: formatBlockReasons(blockedReasons, nextWindow?.label),
            source: "fallback",
        };
    }
    return {
        tradable_now: true,
        trading_status: "SECURITY_TRADING_STATUS_NORMAL_TRADING",
        api_trade_available: true,
        market_order_available: true,
        limit_order_available: true,
        blocked_reasons: [],
        next_tradable_at: null,
        next_tradable_label: null,
        reason: null,
        source: "fallback",
    };
};
